import { randomUUID } from './uuid.ts';
let accessToken = sessionStorage.getItem('companion_access_token') || '';

export class ApiError extends Error {
  status?: number;
  data?: unknown;
}

export type RefreshSessionResult =
  | { status: 'ok'; accessToken: string; authVersion: number }
  | { status: 'unauthorized' }
  | { status: 'unreachable' };

export function setAccessToken(token: string): void {
  accessToken = token;
  if (token) sessionStorage.setItem('companion_access_token', token);
  else sessionStorage.removeItem('companion_access_token');
}

export function getAccessToken(): string { return accessToken; }

let refreshInFlight: Promise<RefreshSessionResult> | null = null;

export async function api<T>(path: string, init: RequestInit = {}, retryAuth = true): Promise<T> {
  const isForm = init.body instanceof FormData;
  let response: Response;
  try {
    response = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
      ...init,
      signal: init.signal ?? AbortSignal.timeout(60000),
      credentials: 'include',
      headers: {
        ...(isForm ? {} : { 'Content-Type': 'application/json' }),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError('FlowNXT ist nicht erreichbar. Offline-Zugang wird verwendet, sofern er freigegeben ist.');
  }
  if (response.status === 401 && retryAuth && !/^\/(api\/)?auth\//.test(path)) {
    if (!refreshInFlight) refreshInFlight = refreshSessionStatus().finally(() => { refreshInFlight = null; });
    const refreshed = await refreshInFlight;
    if (refreshed.status === 'ok') return api<T>(path, init, false);
  }
  const text = await response.text();
  let body: unknown = undefined;
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }
  if (!response.ok) {
    const data = body as { error?: string; reason?: string } | undefined;
    const error = new ApiError(data?.error || data?.reason || `HTTP ${response.status}`);
    error.status = response.status;
    error.data = body;
    throw error;
  }
  return body as T;
}

export async function refreshSession(): Promise<string | null> {
  const result = await refreshSessionStatus();
  return result.status === 'ok' ? result.accessToken : null;
}

export async function refreshSessionStatus(): Promise<RefreshSessionResult> {
  try {
    const result = await api<{ access_token: string; auth_version: number }>('/auth/refresh', { method: 'POST' });
    setAccessToken(result.access_token);
    return { status: 'ok', accessToken: result.access_token, authVersion: result.auth_version };
  } catch (reason) {
    if (reason instanceof ApiError && reason.status === 401) {
      setAccessToken('');
      return { status: 'unauthorized' };
    }
    return { status: 'unreachable' };
  }
}

export function deviceId(): string {
  let value = localStorage.getItem('companion_device_id');
  if (!value) {
    value = randomUUID();
    localStorage.setItem('companion_device_id', value);
  }
  return value;
}

export function position(options: PositionOptions = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) reject(Object.assign(new Error('Standort wird von diesem Gerät nicht unterstützt.'), { code: 0 }));
    else navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}
